import lodashGet from "lodash/get";
import React, { type ComponentPropsWithoutRef } from "react";
import { type PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { getDefaultAddressContactInformationPublicSetting } from "shared/components/pillar-table/helpers/getDefaultAddressContactInformationPublicSetting";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { getWorldBankCountryInformation } from "shared/helpers/profile/address/country/getWorldBankCountryInformation";
import { defaultAddressFromProfile } from "shared/helpers/profile/address/defaultAddressFromProfile";
import { getUSStateInformation } from "shared/helpers/profile/address/us-state/getUSStateInformation";
import { type ProfileView } from "shared/mappers/database/profile/profile";
import { type ProfileAddressView } from "shared/mappers/database/profile/profile-address";

type ProfileAddressProperty = keyof ProfileAddressView | "state-abbreviation";

export type PillarTableColumnProfileAddressProps<T> =
  PillarTableBaseColumnCellValuePathProps<T> &
    ComponentPropsWithoutRef<"div"> & {
      property: ProfileAddressProperty;
      use?: string;
      type?: string;
      /**
       * Only affects `state-abbreviation`. Defaults to `true`: a blank or unrecognized country is not
       * treated as the US, and an unrecognized US state shows as entered, instead of guessing "DC".
       * Pass `false` to fall back to the US / DC like the server-side callers do.
       */
      noFallback?: boolean;
    };

const getDisplayValue = (
  address: ProfileAddressView,
  property: ProfileAddressProperty,
  noFallback: boolean,
) => {
  if (property === "state-abbreviation") {
    if (
      getWorldBankCountryInformation({ country: address.country, noFallback })
        ?.iso3Code === "USA"
    ) {
      return (
        getUSStateInformation({ state: address.state, noFallback })?.iso2code ??
        address.state
      );
    }
    return address.state;
  }
  return address[property]?.toString() ?? "";
};

const PillarTableColumnProfileAddress = <T,>({
  cellValuePath,
  property,
  use,
  type,
  noFallback = true,
  className,
  ...props
}: PillarTableColumnProfileAddressProps<T>) => {
  const session = useCustomSession();
  const { rowValue } = usePillarTableRowContext();

  const profile = cellValuePath
    ? (lodashGet(rowValue, cellValuePath) as ProfileView)
    : (rowValue as ProfileView);

  const defaultAddressSetting =
    getDefaultAddressContactInformationPublicSetting({
      society: session.society,
    });

  const address = defaultAddressFromProfile({
    profile,
    use: use ?? defaultAddressSetting?.use,
    type: type ?? defaultAddressSetting?.type,
  });

  if (!address) {
    return null;
  }

  return (
    <div className={className} {...props}>
      {getDisplayValue(address, property, noFallback)}
    </div>
  );
};

export default PillarTableColumnProfileAddress;
